import type { AprBlock } from '../spec/types'

/**
 * The maker-checker approval card: what is at stake, who signed, who still has to.
 *
 * Verbatim from `docs/design/reference/blocks/apr.html`. A summary strip of the
 * action's facts sits on a 2px rule above exactly two slots, and a footer states
 * what countersignature actually causes.
 *
 * Law 15 is the whole shape of this block. Both slots render from the first
 * paint — before either is filled — so the second signature reads as a standing
 * requirement rather than as a surprise raised at the moment someone tries to
 * act. There is deliberately no single Approve button anywhere in it.
 *
 * The checker button's chrome keys off the SLOT's own disabled flag, not off
 * `writeBlocked`: a maker looking at their own action must see a permanently
 * unavailable control with the reason stated beside it, which is a different
 * fact from "this cell is standby and nothing is writable right now" (law 18).
 * The dim treatment is the union of the two — either reason dims the slot.
 *
 * Summary values arrive pre-composed as strings in the spec, each with its
 * ISO-4217 code in the adjacent column and an empty code where the figure is a
 * count rather than money. That is not a missed `formatMinor` call: the block
 * shows the figure the approval was raised against, and law 8 is satisfied by
 * the code being carried next to it rather than by reformatting it here.
 */

interface Props {
  readonly block: AprBlock
  /** Standby cell, or a session whose role has no write scope — law 18. */
  readonly writeBlocked: boolean
}

export function Apr({ block, writeBlocked }: Props) {
  return (
    <div
      style={{ border: '1px solid var(--k-line)', background: 'var(--k-panel)', maxWidth: '860px' }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
          gap: '1px',
          background: 'var(--k-line)',
          borderBottom: '2px solid var(--k-rule)',
        }}
      >
        {block.summary.map(([k, v, cur]) => (
          <div key={k} style={{ background: 'var(--k-panel)', padding: '9px 11px' }}>
            <div
              style={{
                fontSize: '10px',
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'var(--k-mut2)',
                fontFamily: 'var(--k-mono)',
              }}
            >
              {k}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '4px' }}>
              <span style={{ fontWeight: 700, fontSize: '15px' }}>{v}</span>
              <span
                style={{ fontFamily: 'var(--k-mono)', fontSize: '10px', color: 'var(--k-mut)' }}
              >
                {cur}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1px',
          background: 'var(--k-line)',
        }}
      >
        {block.slots.map(([label, state, tone, actor, at, reason, btn, disabled, edge]) => (
          <div
            key={label}
            data-edge={edge || 'n'}
            style={{ background: 'var(--k-panel)', padding: '12px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <span
                style={{
                  fontSize: '10.5px',
                  fontWeight: 800,
                  letterSpacing: '.07em',
                  textTransform: 'uppercase',
                }}
              >
                {label}
              </span>
              <span
                data-tone={tone}
                style={{
                  padding: '0 5px',
                  fontSize: '9.5px',
                  fontWeight: 800,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                }}
              >
                {state}
              </span>
            </div>
            <div style={{ fontSize: '12.5px', marginTop: '7px', fontWeight: 600 }}>{actor}</div>
            <div
              style={{
                fontFamily: 'var(--k-mono)',
                fontSize: '11px',
                color: 'var(--k-mut)',
                marginTop: '2px',
              }}
            >
              {at}
            </div>
            {/* The reason box holds its height whether or not a reason has been
                given, so an unsigned slot does not shuffle the button upward
                when the counterpart slot is filled. */}
            <div
              style={{
                fontSize: '11.5px',
                color: 'var(--k-mut)',
                marginTop: '7px',
                minHeight: '2.6em',
                textWrap: 'pretty',
              }}
            >
              {reason}
            </div>
            <button
              type="button"
              /* Disabled in the DOM, not merely dimmed. The slot's `disabled`
                 flag drove the colours and nothing else, so the control was
                 focusable, clickable and silently inert — a fabricated
                 affordance of exactly the kind law 18 exists to stop, wearing
                 the styling of an honest one. Nothing here posts in any branch
                 (the act is a `form` below), so the attribute is unconditional
                 on the slot's own flag as well as the shell's. */
              disabled={disabled || writeBlocked}
              data-dim={disabled || writeBlocked ? '1' : ''}
              style={{
                height: '28px',
                padding: '0 12px',
                marginTop: '6px',
                border: disabled ? '1px solid var(--k-line)' : '0',
                background: disabled ? 'transparent' : 'var(--k-accent)',
                color: disabled ? 'var(--k-mut)' : 'var(--k-accentink)',
                font: 'inherit',
                fontWeight: 800,
                fontSize: '12px',
                cursor: disabled || writeBlocked ? 'not-allowed' : 'pointer',
              }}
            >
              {btn}
            </button>
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '9px 12px',
          borderTop: '1px solid var(--k-line)',
          background: 'var(--k-bg)',
          fontSize: '11.5px',
          color: 'var(--k-mut)',
        }}
      >
        {block.note2}
      </div>
    </div>
  )
}
